import { PaymentSystem } from '@web-solutions/react-billing';

import { extractDeclineCode, extractResponseCode } from './get-payment-error-content';

/**
 * The four states the payment-error popup can show. One layout, only the text
 * and the button change — the state is picked from the reason the payment
 * system reported.
 *
 * 1 BANK_DECLINED  — the card reached the bank and the bank refused it.
 * 2 CARD_DATA      — the payment system pointed at a typo in the card fields.
 * 3 TEMPORARY      — the charge never reached the bank, plus every unknown reason.
 * 4 NOT_CONFIRMED  — the bank asked for confirmation and it wasn't completed.
 */
export enum PaymentErrorState {
  BANK_DECLINED = 1,
  CARD_DATA = 2,
  TEMPORARY = 3,
  NOT_CONFIRMED = 4,
}

/** Where the failure happened — reported to analytics alongside the state. */
export type PaymentErrorPlace = 'purchase' | 'upsale';

export type PaymentErrorContext = {
  paymentSystem?: PaymentSystem | string;
  /** 'card' | 'applepay' | 'googlepay' | 'paypal' | 'mercadopago' */
  method?: string;
  place?: PaymentErrorPlace;
};

/**
 * Wallet payments: the user never typed a card, so a "check your card details"
 * message is meaningless to them. Stripe still returns card-data codes for a
 * tokenized wallet card, and Stripe's own guidance is to show those as a plain
 * decline.
 */
const WALLET_METHODS = ['applepay', 'googlepay', 'apple_pay', 'google_pay', 'paypal'];

const isWalletMethod = (method?: string) =>
  !!method && WALLET_METHODS.includes(method.toLowerCase());

/** Stripe: the bank was reached and refused. */
const STRIPE_BANK_DECLINE_CODES = [
  'insufficient_funds',
  'lost_card',
  'stolen_card',
  'pickup_card',
  'restricted_card',
  'do_not_honor',
  'generic_decline',
  'fraudulent',
  'merchant_blacklist',
  'card_velocity_exceeded',
  'withdrawal_count_limit_exceeded',
  'transaction_not_allowed',
  'service_not_allowed',
  'security_violation',
  'currency_not_supported',
  'card_not_supported',
  'not_permitted',
  'revocation_of_all_authorizations',
  'revocation_of_authorization',
  'stop_payment_order',
  'no_action_taken',
  'offline_pin_required',
  'online_or_offline_pin_required',
  'pin_try_exceeded',
  'new_account_information_available',
  'card_decline_rate_limit_exceeded',
];

/** Stripe: the card fields themselves are wrong. */
const STRIPE_CARD_DATA_CODES = [
  'incorrect_number',
  'invalid_number',
  'incorrect_cvc',
  'invalid_cvc',
  'incorrect_zip',
  'invalid_expiry_month',
  'invalid_expiry_year',
  'invalid_expiry_year_past',
  'expired_card',
  'invalid_account',
  'incorrect_pin',
  'invalid_characters',
];

/** Stripe: the bank asked for 3-D Secure and it wasn't completed. */
const STRIPE_AUTHENTICATION_CODES = [
  'authentication_required',
  'payment_intent_authentication_failure',
  'setup_intent_authentication_failure',
  'authentication_not_handled',
];

/**
 * Stripe: never reached the bank, or the bank told us to just retry — both are
 * "try again", not "your card is bad".
 */
const STRIPE_TEMPORARY_CODES = [
  'processing_error',
  'issuer_not_available',
  'try_again_later',
  'reenter_transaction',
  'duplicate_transaction',
  'approve_with_id',
  'api_error',
  'api_connection_error',
  'rate_limit',
  'lock_timeout',
  'invalid_amount',
];

/**
 * Solidgate reports everything through its numeric code. Grouped along the same
 * lines the existing detailed-error popup already groups them
 * (see error-popup/utils/get-content-by-code.ts).
 */
const SOLIDGATE_CARD_DATA_CODES = ['201', '208', '209', '701'];

const SOLIDGATE_AUTHENTICATION_CODES = ['206', '311', '312'];

const SOLIDGATE_TEMPORARY_CODES = [
  '214',
  '502',
  '503',
  '504',
  '505',
  '507',
  '509',
  '510',
  '511',
  '702',
  '703',
  '704',
  '705',
  '706',
];

const normalizeCode = (code: unknown): string =>
  typeof code === 'string' || typeof code === 'number'
    ? String(code).trim().toLowerCase().replace(/\./g, '')
    : '';

/**
 * Where the billing interceptor (packages/module-billing/api/network.js) stamps
 * the transport's HTTP status, on both the resolved body and the error it
 * throws. Kept as a literal so core doesn't take a dependency on module-billing.
 */
const HTTP_STATUS_KEY = '__http_status';

/**
 * HTTP status of the failed request. Our own 502 is the single most common
 * technical failure and it arrives with no `code` at all (the billing
 * interceptor only enriches responses that carry a `message`), so without this
 * it is indistinguishable from any other glitch in the analytics.
 *
 * Read only from the transport: `__http_status` for anything that went through
 * the billing interceptor, `response.status` for a raw axios error. A bare
 * `status` on the error or inside its body is the backend's own field
 * ('failed', 'requires_action', a Stripe intent status) — reading that is what
 * previously put non-HTTP values, or nothing at all, in this property.
 */
export const getHttpStatus = (error: any): number | undefined => {
  const status =
    error?.[HTTP_STATUS_KEY] ??
    error?.response?.status ??
    error?.statusCode;

  const parsed = Number(status);
  // Range-guarded so a payload field that happens to be numeric can never be
  // reported as an HTTP status.
  return Number.isFinite(parsed) && parsed >= 100 && parsed <= 599 ? parsed : undefined;
};

/**
 * A reason that came from the payment provider: the bank's decline code, or a
 * response code one of the tables above recognizes. Its presence means the
 * charge did reach the provider and the failure has a real, classifiable cause.
 *
 * Needed because our own backend forwards a bank decline as an HTTP 502 (seen
 * on stage: 502 carrying `card_declined` / `insufficient_funds`). The status
 * alone therefore says nothing about whose fault it was, so it must not outrank
 * a reason the provider actually gave.
 */
const hasProviderReason = (error: any): boolean => {
  if (normalizeCode(extractDeclineCode(error, PaymentSystem.STRIPE))) {
    return true;
  }

  const code = normalizeCode(extractResponseCode(error));

  if (!code) {
    return false;
  }

  return (
    code === 'card_declined' ||
    STRIPE_BANK_DECLINE_CODES.includes(code) ||
    STRIPE_CARD_DATA_CODES.includes(code) ||
    STRIPE_AUTHENTICATION_CODES.includes(code) ||
    STRIPE_TEMPORARY_CODES.includes(code) ||
    SOLIDGATE_CARD_DATA_CODES.includes(code) ||
    SOLIDGATE_AUTHENTICATION_CODES.includes(code) ||
    SOLIDGATE_TEMPORARY_CODES.includes(code) ||
    // Solidgate puts everything in a numeric code; any of them is a real answer
    // from the provider, so the code tables below get to classify it.
    /^\d+$/.test(code)
  );
};

const isNetworkOrServerFailure = (error: any): boolean => {
  const axiosCode = normalizeCode(error?.code);

  if (
    axiosCode === 'err_bad_response' ||
    axiosCode === 'err_network' ||
    axiosCode === 'econnaborted' ||
    axiosCode === 'etimedout' ||
    error?.message === 'Network Error'
  ) {
    return true;
  }

  const status = getHttpStatus(error);

  if (!status || status < 500) {
    return false;
  }

  // A 5xx with no provider reason attached is genuinely our own failure — that
  // is the bare 502 the comment on `getHttpStatus` describes. A 5xx that does
  // carry a reason is a decline our backend wrapped, and the tables below must
  // decide the state, or every declined card collapses into state 3.
  return !hasProviderReason(error);
};

/**
 * The user closed the bank's confirmation window, or the confirmation itself
 * failed. Stripe reports this through a code, but a payment intent that came
 * back still needing action after we asked the user to confirm means the same
 * thing.
 */
const isAuthenticationFailure = (error: any): boolean => {
  const code = normalizeCode(error?.code);
  const declineCode = normalizeCode(error?.decline_code || error?.declineCode);
  const intentStatus = normalizeCode(
    error?.payment_intent_status || error?.paymentIntentStatus,
  );

  return (
    STRIPE_AUTHENTICATION_CODES.includes(code) ||
    STRIPE_AUTHENTICATION_CODES.includes(declineCode) ||
    SOLIDGATE_AUTHENTICATION_CODES.includes(code) ||
    intentStatus === 'requires_action' ||
    intentStatus === 'requires_confirmation' ||
    error?.threeDSecureFailed === true
  );
};

const resolveStripeState = (error: any, isWallet: boolean): PaymentErrorState => {
  const code = normalizeCode(error?.code);
  const declineCode = normalizeCode(extractDeclineCode(error, PaymentSystem.STRIPE));

  if (STRIPE_TEMPORARY_CODES.includes(code) || STRIPE_TEMPORARY_CODES.includes(declineCode)) {
    return PaymentErrorState.TEMPORARY;
  }

  if (STRIPE_BANK_DECLINE_CODES.includes(declineCode) || STRIPE_BANK_DECLINE_CODES.includes(code)) {
    return PaymentErrorState.BANK_DECLINED;
  }

  if (STRIPE_CARD_DATA_CODES.includes(code) || STRIPE_CARD_DATA_CODES.includes(declineCode)) {
    // Apple Pay / Google Pay: no card was typed, so state 2 would be nonsense.
    return isWallet ? PaymentErrorState.BANK_DECLINED : PaymentErrorState.CARD_DATA;
  }

  // `card_declined` with no usable decline reason still means the bank refused.
  if (code === 'card_declined') {
    return PaymentErrorState.BANK_DECLINED;
  }

  return PaymentErrorState.TEMPORARY;
};

const resolveSolidgateState = (error: any, isWallet: boolean): PaymentErrorState => {
  const code = normalizeCode(error?.code);

  if (!code) {
    return PaymentErrorState.TEMPORARY;
  }

  if (SOLIDGATE_TEMPORARY_CODES.includes(code)) {
    return PaymentErrorState.TEMPORARY;
  }

  if (SOLIDGATE_CARD_DATA_CODES.includes(code)) {
    return isWallet ? PaymentErrorState.BANK_DECLINED : PaymentErrorState.CARD_DATA;
  }

  // 0.xx / 1.xx / 2.1x / 3.xx / 4.xx / 6.xx are issuer-side refusals.
  if (/^(0|1|3|4|6)\d{2}$/.test(code) || /^2(1[0-3]|15)$/.test(code)) {
    return PaymentErrorState.BANK_DECLINED;
  }

  return PaymentErrorState.TEMPORARY;
};

/**
 * Pick the popup state for a failed payment.
 *
 * Order matters: a failed bank confirmation and a transport failure both come
 * back looking like a decline, so they are checked before the provider-specific
 * code tables.
 */
export const resolvePaymentErrorState = (
  error: any,
  context: PaymentErrorContext = {},
): PaymentErrorState => {
  if (isAuthenticationFailure(error)) {
    return PaymentErrorState.NOT_CONFIRMED;
  }

  if (isNetworkOrServerFailure(error)) {
    return PaymentErrorState.TEMPORARY;
  }

  const isWallet = isWalletMethod(context.method || error?.method);
  const paymentSystem = error?.paymentSystem || context.paymentSystem;

  if (paymentSystem === PaymentSystem.SOLIDGATE) {
    return resolveSolidgateState(error, isWallet);
  }

  return resolveStripeState(error, isWallet);
};

/**
 * Everything the funnel must report to PostHog with a payment failure: the
 * state the user was shown, how they were paying, where it happened and the
 * response code — including the HTTP status, which is the only thing a bare
 * 502 from our own server carries.
 */
export const getPaymentErrorAnalyticsParams = (
  error: any,
  context: PaymentErrorContext = {},
) => {
  const paymentSystem = error?.paymentSystem || context.paymentSystem;

  return {
    error_state: resolvePaymentErrorState(error, context),
    place: context.place || 'purchase',
    method: context.method || error?.method,
    paymentSystem,
    code: extractResponseCode(error),
    decline_code: extractDeclineCode(error, paymentSystem),
    http_status: getHttpStatus(error),
  };
};
